import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Menu, MenuButton, MenuItem, MenuItems } from '@headlessui/react';

import { LogoutIcon, SettingsIcon } from './Icons';
import { LogoutConfirmationModal } from './LogoutConfirmationModal';
import { useSessionState } from '@/context/SessionContext';

function getInitials(name: string | null) {
  if (!name) {
    return 'SL';
  }

  return name
    .split(' ')
    .map((part) => part.trim().charAt(0))
    .filter(Boolean)
    .slice(0, 2)
    .join('')
    .toUpperCase();
}

export function AccountMenu() {
  const navigate = useNavigate();
  const { sessionName, sessionRole, sessionUsername, signOut } = useSessionState();
  const roleLabel = sessionRole === 'staff' ? 'Staff' : 'Admin';
  const [isLogoutOpen, setIsLogoutOpen] = useState(false);
  const [isSigningOut, setIsSigningOut] = useState(false);

  async function handleSignOut() {
    if (isSigningOut) {
      return;
    }

    setIsSigningOut(true);

    try {
      await signOut();
      setIsLogoutOpen(false);
      navigate('/login', { replace: true });
    } finally {
      setIsSigningOut(false);
    }
  }

  return (
    <Menu as="div" className="relative">
      <MenuButton
        className="avatar inline-flex items-center justify-center focus:outline-none focus:ring-2 focus:ring-[color:var(--accent)] focus:ring-offset-2 focus:ring-offset-[color:var(--bg)]"
        aria-label="Open account menu"
        title={sessionName ?? 'SlotLooks account'}>
        {getInitials(sessionName)}
      </MenuButton>

      <MenuItems anchor="bottom end" className="menu-panel" transition>
        <div className="menu-panel-header">
          <div className="menu-panel-kicker">Signed in</div>
          <div className="menu-panel-title">{sessionName ?? 'SlotLooks'}</div>
          <div className="menu-panel-subtitle">
            {sessionUsername ?? 'No username saved'} {sessionRole ? `· ${roleLabel}` : ''}
          </div>
        </div>

        <div className="menu-list">
          <MenuItem>
            {({ focus }) => (
              <button
                type="button"
                className={`menu-option center${focus ? ' active' : ''}`}
                onClick={() => {
                  navigate('/settings');
                }}>
                <span className="menu-option-icon">
                  <SettingsIcon size={18} />
                </span>
                <span className="menu-option-content">
                  <strong className="menu-option-title">Open settings</strong>
                  <span className="menu-option-text">Theme, password, and session details</span>
                </span>
              </button>
            )}
          </MenuItem>

          <MenuItem>
            {({ focus }) => (
              <button
                type="button"
                className={`menu-option center${focus ? ' active' : ''}`}
                onClick={() => {
                  setIsLogoutOpen(true);
                }}>
                <span className="menu-option-icon">
                  <LogoutIcon size={18} />
                </span>
                <span className="menu-option-content">
                  <strong className="menu-option-title">Sign out</strong>
                  <span className="menu-option-text">Leave the current web session</span>
                </span>
              </button>
            )}
          </MenuItem>
        </div>
      </MenuItems>

      <LogoutConfirmationModal
        open={isLogoutOpen}
        isSigningOut={isSigningOut}
        accountName={sessionName ?? sessionUsername}
        onCancel={() => setIsLogoutOpen(false)}
        onConfirm={() => {
          void handleSignOut();
        }}
      />
    </Menu>
  );
}
